"use client"

import type { FullTranslation } from "../lib/i18n"
import {
  useAutoleadsLocale,
  useSetAutoleadsLocale,
} from "../lib/locale-context"
import { setLocaleCookie } from "../lib/locale-cookie"
import { isNonLocalizedPath, mirrorPath } from "../lib/locale-path"
import { useTr } from "../lib/use-tr"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"

const LOCALE_SWITCHER_T = {
  switchLabel: { FR: "Switch to English", EN: "Passer en français" },
} satisfies FullTranslation

/**
 * Public pages carry their locale in the URL and the areas with no /fr mirror
 * carry it in a cookie, so switching is a navigation on one side and a state
 * change on the other.
 */
export function LocaleSwitcher({ className }: { className?: string }) {
  const pathname = usePathname()
  return isNonLocalizedPath(pathname) ? (
    <CookieLocaleSwitcher className={className} />
  ) : (
    <PathLocaleSwitcher className={className} />
  )
}

function PathLocaleSwitcher({ className }: { className?: string }) {
  const pathname = usePathname()
  const router = useRouter()
  const locale = useAutoleadsLocale()
  const tr = useTr(LOCALE_SWITCHER_T)
  const next = locale === "FR" ? "EN" : "FR"
  const target = mirrorPath(pathname, next)
  return (
    <Link
      href={target}
      hrefLang={next === "FR" ? "fr" : "en"}
      aria-label={tr("switchLabel")}
      onClick={(event) => {
        event.preventDefault()
        // Recorded before leaving: the root URL redirects French visitors to
        // /fr, so the switch away from /fr would otherwise bounce straight back.
        setLocaleCookie(next)
        // Both locales of a page rewrite to the same internal route, so the
        // router would serve the tree it already holds; refresh discards it.
        // scroll: false keeps the visitor where they were reading.
        router.push(target, { scroll: false })
        router.refresh()
      }}
      className={className}
    >
      {next}
    </Link>
  )
}

function CookieLocaleSwitcher({ className }: { className?: string }) {
  const locale = useAutoleadsLocale()
  const setLocale = useSetAutoleadsLocale()
  const tr = useTr(LOCALE_SWITCHER_T)
  const next = locale === "FR" ? "EN" : "FR"
  return (
    <button
      type="button"
      onClick={() => setLocale(next)}
      aria-label={tr("switchLabel")}
      className={className}
    >
      {next}
    </button>
  )
}
